import { type ChangeEvent } from 'react'

import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { lighten } from '@mui/material/styles'
import { useDebouncedCallback } from '@react-hookz/web'
import { type Filter, type FilterValue } from '~/api/product-api'
import FilterSelect from '~/oakra/components/filter-select'

type Props = {
    numSelected?: number
    page?: number
    count?: number
    loading?: boolean
    filterOptions?: Filter[]
    filterValues: Record<string, unknown>
    onDelete: () => void
    onNewProductClick: () => void
    onMakeActive: () => void
    onMakeInactive: () => void
    onSearchChange: (value: string) => void
    onFilterChange: (field: Filter) => (option: FilterValue | null) => void
    onSelectExclusive: (field: Filter) => (e: ChangeEvent<HTMLInputElement>) => void
}

export default function ProductsToolbar({
    numSelected = 0,
    loading = false,
    filterOptions,
    filterValues,
    onFilterChange,
    onSelectExclusive,
    onNewProductClick,
    onDelete,
    onMakeActive,
    onMakeInactive,
    onSearchChange,
}: Props) {
    const onDebouncedSearchChange = useDebouncedCallback(onSearchChange, [], 500)
    const hasSelected = numSelected > 0

    return (
        <Toolbar
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                maxHeight: 'var(--products-toolbar-max-height)',
                color: (theme) => (hasSelected ? theme.palette.secondary.main : 'inherit'),
                backgroundColor: (theme) =>
                    hasSelected
                        ? lighten(theme.palette.secondary.light, 0.5)
                        : theme.palette.background.paper,
            }}
        >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', maxWidth: '80%' }}>
                {numSelected > 0 ? (
                    <Typography color="inherit" variant="subtitle1">
                        {interpolate(pgettext('Number selected', '%s selected'), [numSelected])}
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexGrow: 1, flexWrap: 'wrap', my: 1 }}>
                        {loading &&
                            Array.from({ length: 8 }).map((_, i) => (
                                <Skeleton
                                    key={i}
                                    width={150}
                                    height={36}
                                    sx={{ width: 150, my: 0.25, mr: 1 }}
                                />
                            ))}
                        {filterOptions && (
                            <>
                                <TextField
                                    fullWidth
                                    disabled={loading}
                                    id="search"
                                    type="search"
                                    margin="normal"
                                    variant="outlined"
                                    sx={{ maxWidth: 150, my: 0.5, mr: 1 }}
                                    placeholder={gettext('Search products')}
                                    InputProps={{ sx: { height: 30, fontSize: 13 } }}
                                    inputProps={{ style: { paddingInline: 10 } }}
                                    onChange={(e) => onDebouncedSearchChange(e.target.value)}
                                />
                                {filterOptions.map((field) => (
                                    <FilterSelect
                                        disabled={loading}
                                        key={field.lookup}
                                        field={field}
                                        value={filterValues[field.lookup]}
                                        handleFilterChange={onFilterChange}
                                        handleFilterSelectExclusive={onSelectExclusive}
                                    />
                                ))}
                            </>
                        )}
                    </Box>
                )}
            </Box>

            {hasSelected ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button color="secondary" onClick={onMakeActive}>
                        {gettext('Make active')}
                    </Button>
                    <Button color="secondary" onClick={onMakeInactive}>
                        {gettext('Make inactive')}
                    </Button>
                    <Button color="secondary" onClick={onDelete}>
                        {gettext('Delete')}
                    </Button>
                </Box>
            ) : (
                <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    sx={{ flexShrink: 0 }}
                    startIcon={<AddIcon />}
                    onClick={onNewProductClick}
                >
                    {gettext('New product')}
                </Button>
            )}
        </Toolbar>
    )
}
